import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/pages/Portal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const FIELD_GROUPS = [
  { title: "Company Identity", fields: [
    ["legal_name", "Legal Company Name"], ["gstin", "GSTIN"], ["pan", "PAN"], ["cin", "CIN"],
  ] },
  { title: "Contact & Address", fields: [
    ["address", "Business Address"], ["phone", "Phone"], ["email", "Email"], ["website", "Website"], ["state", "State (for CGST/SGST vs IGST)"],
  ] },
  { title: "Bank Details (shown on Tax Invoices)", fields: [
    ["bank_account_name", "Account Holder Name"], ["bank_name", "Bank Name"], ["account_number", "Account Number"], ["ifsc", "IFSC Code"], ["branch", "Branch"],
  ] },
];

export default function CompanySettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/company-settings").then((r) => setForm(r.data)).catch((e) => toast.error(apiErr(e))); }, []);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = form;
      const r = await api.put("/company-settings", payload);
      setForm(r.data);
      toast.success("Company settings saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  if (!form) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="p-8 max-w-3xl">
      <PageHeader title="Company Settings" subtitle="Billing Configuration">
        <Button onClick={save} disabled={saving} className="rounded-sm bg-primary hover:bg-primary/90 gap-2" data-testid="save-company-settings-btn">
          <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
        </Button>
      </PageHeader>

      <p className="text-sm text-muted-foreground mb-6 -mt-4">
        These details are snapshotted onto every new quotation/invoice PDF at the time it's generated — editing them here will not change documents already issued.
      </p>

      <div className="space-y-8">
        {FIELD_GROUPS.map((group) => (
          <div key={group.title} className="bg-card border border-border rounded-sm p-5">
            <h3 className="font-heading font-semibold text-secondary mb-4">{group.title}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {group.fields.map(([key, label]) => (
                <div key={key}>
                  <Label className="text-xs uppercase">{label}</Label>
                  <Input value={form[key] || ""} onChange={(e) => set(key, e.target.value)} className="rounded-sm mt-1" data-testid={`company-${key}`} />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="bg-card border border-border rounded-sm p-5 space-y-4">
          <h3 className="font-heading font-semibold text-secondary">Defaults for New Quotations</h3>
          <div>
            <Label className="text-xs uppercase">Default Terms &amp; Conditions</Label>
            <Textarea rows={4} value={form.default_terms || ""} onChange={(e) => set("default_terms", e.target.value)} className="rounded-sm mt-1" data-testid="company-default-terms" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase">Default Validity (days)</Label>
              <Input type="number" value={form.default_validity_days || ""} onChange={(e) => set("default_validity_days", parseInt(e.target.value) || 0)} className="rounded-sm mt-1" data-testid="company-default-validity" />
            </div>
            <div>
              <Label className="text-xs uppercase">Authorised Signatory Title</Label>
              <Input value={form.authorized_signatory || ""} onChange={(e) => set("authorized_signatory", e.target.value)} className="rounded-sm mt-1" data-testid="company-signatory" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase">Default Payment Terms</Label>
            <Textarea rows={2} value={form.default_payment_terms || ""} onChange={(e) => set("default_payment_terms", e.target.value)} className="rounded-sm mt-1" data-testid="company-payment-terms" />
          </div>
        </div>
      </div>
    </div>
  );
}
